// درخواست خرید کالا / خدمات
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList, CountBadge } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, qs, toFa, curYear, splitLines, visibleCols, runReport, MsgBox, AskBox, Field, DateField, SelectField, Box,
  Lookup, Grid, Modal, SubFormShell, tbtn,
} from './RequestsShared';
import GoodsRequestDetail, { DETAIL_COLS } from './GoodsRequestDetail';
import GoodsRequestAllReports from './GoodsRequestAllReports';

const FORM = 'FRMGOODSREQUEST';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNGOODSREQUESTDETAIL', 'BTNGOODSREQUESTREPORT',
  'BTNGOODSREQUESTREPORTWITHESTIMATECOST', 'BTNCONSUME', 'BTNSERVICECONFIRM', 'BTNPROPERTYALLREPORTS', 'BTNMOVEREQUEST',
  'BTNREMARKPIN', 'BTNREMARKUNPIN'];
const TITLE = 'درخواست خرید کالا / خدمات';
export const REQUEST_KINDS = ['خرید کالای مصرفی', 'خرید کالای اموالی', 'انجام خدمات', 'خرید کالای مصرفی تحویل مستقیم',
  'سند ترکیبی (اموالی-مصرفی-خدمات)'];
const ACCOUNTING_KINDS = ['هزینه ای', 'تملک'];

export const COLS = [
  { key: 'accounting_kind_title', label: 'هزینه ای/تملک' },
  { key: 'request_kind_title', label: 'نوع درخواست' },
  { key: 'request_no', label: 'درخواست', num: true },
  { key: 'request_date', label: 'تاریخ' },
  { key: 'confirm_no', label: 'تاییدیه', num: true },
  { key: 'confirm_date', label: 'تاریخ تاییدیه' },
  { key: 'asker', label: 'درخواست کننده', wrap: true },
  { key: 'department', label: 'واحد اجرایی', wrap: true },
  { key: 'remark', label: 'شرح', wrap: true },
  { key: 'car', label: 'خودرو', wrap: true },
  { key: 'building', label: 'ساختمان', wrap: true },
  { key: 'detail_count', label: 'تعداد ریز', num: true },
  { key: 'estimated_cost', label: 'مبلغ برآورد', money: true },
  { key: 'buy_amount', label: 'مبلغ خرید', money: true },
  { key: 'added_tax', label: 'مالیات بر ارزش افزوده', money: true },
  { key: 'total_amount', label: 'جمع کل درخواست', money: true },
  { key: 'factor_discount', label: 'تخفیف کلی فاکتور', money: true },
  { key: 'user_group', label: 'گروه کاربری' },
];

const KEYS = ['accounting_kind', 'request_kind', 'request_no', 'request_date', 'confirm_no', 'confirm_date',
  'asker_id', 'asker', 'department_id', 'department', 'car_id', 'car', 'building_id', 'building', 'remark'];
const pick = r => KEYS.reduce((o, k) => ({ ...o, [k]: r ? (r[k] ?? '') : '' }), {});

export default function GoodsRequest({ user, onBack }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const fbDetail = useFormButtons('FRMGOODSREQUESTDETAIL', []);
  const [year, setYear] = useState(curYear());
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(pick(null));
  const [kindLocked, setKindLocked] = useState(false);
  const [remarkPinned, setRemarkPinned] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(null);
  const [move, setMove] = useState(null);
  const [sub, setSub] = useState(null);

  const L = usePagedList((page, o) => API(`/goods/?${qs({ page, year: o.year, search: o.search })}`));
  const reload = useCallback((keepId, y) => {
    L.reload({ year: y || year, search }).then(j => {
      if (!j || !j.ok) return;
      const rows = j.data || [];
      const hit = keepId ? rows.find(r => r.id === keepId) : null;
      setSelected(hit || rows[0] || null);
    });
  }, [L, year, search]);

  useEffect(() => { if (fb.loaded && fb.open) reload(); }, [fb.loaded, fb.open]);
  useEffect(() => {
    if (!selected) { setDetails([]); return; }
    API(`/goods/${selected.id}/details/`).then(j => setDetails(j.ok ? j.data : []));
  }, [selected]);

  const showMsg = m => setMsg(splitLines(m));
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const has = !!selected;

  const openNew = async () => {
    const j = await API(`/goods/request-no/?year=${year}`);
    if (!j.ok) { showMsg(j.msg); return; }
    const s = selected || {};
    setForm({
      ...pick(null), accounting_kind: s.accounting_kind || 0, request_kind: s.request_kind || 0,
      asker_id: s.asker_id || 0, asker: s.asker || '', department_id: s.department_id || 0, department: s.department || '',
      remark: remarkPinned ? (s.remark || '') : '', request_no: j.request_no, confirm_no: j.confirm_no,
    });
    setKindLocked(false); setFormErr(''); setModal('new');
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const openEdit = async () => {
    if (!selected) return;
    const j = await API(`/goods/${selected.id}/check-edit/`);
    if (!j.ok) { showMsg(j.msg); return; }
    setForm(pick(selected)); setKindLocked(!!j.kind_locked); setFormErr(''); setModal('edit');
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const save = async () => {
    setFormErr(''); setSaving(true);
    const body = JSON.stringify({ ...form, year });
    const j = modal === 'new'
      ? await API('/goods/create/', { method: 'POST', body })
      : await API(`/goods/${selected.id}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (!j.ok) { setFormErr(j.msg); return; }
    setModal(null); reload(j.id);
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const doDelete = async () => {
    setAsk(null);
    const j = await API(`/goods/${selected.id}/delete/`, { method: 'DELETE' });
    if (j.ok) reload(); else showMsg(j.msg);
  };

  const openDetail = () => { if (selected && fbDetail.open) setSub('detail'); };
  const closeDetail = () => { setSub(null); reload(selected && selected.id); };

  const formReport = (key) => {
    if (!selected) return;
    const run = wf => runReport(key, { id: selected.id, with_factor: wf ? 1 : 0 }, showMsg);
    if (details.some(r => String(r.factor_no || '').trim())) {
      setAsk({ title: 'انتخاب', lines: ['مشخصات فاکتور در گزارش قید شود؟'], onYes: () => { setAsk(null); run(true); },
        onNo: () => { setAsk(null); run(false); } });
    } else run(false);
  };

  const movePreview = async () => {
    const j = await API(`/goods/${selected.id}/move/?to_year=${encodeURIComponent(move.to_year || '')}`);
    if (!j.ok) { showMsg(j.msg); return; }
    setAsk({ title: 'هشدار', lines: [j.question], onYes: () => doMove(j.to_year), onNo: () => setAsk(null) });
  };
  const doMove = async (toYear) => {
    setAsk(null);
    const j = await API(`/goods/${selected.id}/move/`, { method: 'POST', body: JSON.stringify({ to_year: toYear }) });
    if (!j.ok) { showMsg(j.msg); return; }
    setMove(null); setYear(j.to_year); reload(selected.id, j.to_year);
  };

  const shown = visibleCols(COLS, L.data);
  const dshown = visibleCols(DETAIL_COLS, details);
  const canService = has && Number(selected.request_kind) >= 2;
  const toolbar = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={ui.flabel}>سال مالی</span>
        <input value={year} onChange={e => setYear(Number(e.target.value) || '')} style={ui.yearInput}
          onKeyDown={e => e.key === 'Enter' && reload()} onBlur={() => reload()} />
      </div>
      {fb.open && (
        <div style={{ ...ui.toolBtns, flexWrap: 'wrap' }}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={tbtn(ui.btnNew)}>＋ جدید</button>}
          {fb.can('BTNEDIT') && L.data.length > 0 && <button onClick={openEdit} disabled={!has} style={tbtn(ui.btnEdit, has)}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && L.data.length > 0 && (
            <button onClick={() => has && setAsk({ lines: ['آيا از حذف اين رکورد مطمئن هستيد؟'], danger: true, onYes: doDelete,
              onNo: () => setAsk(null) })} disabled={!has} style={tbtn(ui.btnDel, has)}>✕ حذف</button>)}
          {fb.can('BTNGOODSREQUESTDETAIL') && <button onClick={openDetail} disabled={!has} style={tbtn(ui.btnItems, has)}>ریز درخواست</button>}
          {fb.can('BTNGOODSREQUESTREPORT') && <button onClick={() => formReport('tadarokat.goods_request_form')} disabled={!has} style={tbtn(ui.btnEdit, has)}>فرم درخواست</button>}
          {fb.can('BTNGOODSREQUESTREPORTWITHESTIMATECOST') && <button onClick={() => formReport('tadarokat.goods_request_estimate')} disabled={!has} style={tbtn(ui.btnEdit, has)}>فرم درخواست با برآورد</button>}
          {fb.can('BTNCONSUME') && <button onClick={() => has && runReport('tadarokat.goods_request_consume', { id: selected.id }, showMsg)} disabled={!has} style={tbtn(ui.btnEdit, has)}>صورتجلسه مصرف</button>}
          {fb.can('BTNSERVICECONFIRM') && canService && <button onClick={() => runReport('tadarokat.goods_request_service_confirm', { id: selected.id }, showMsg)} style={tbtn(ui.btnEdit)}>تایید کار</button>}
          {fb.can('BTNPROPERTYALLREPORTS') && <button onClick={() => setSub('all')} style={tbtn(ui.btnItems)}>گزارش کلی</button>}
          {fb.can('BTNMOVEREQUEST') && L.data.length > 0 && <button onClick={() => has && setMove({ to_year: '' })} disabled={!has} style={tbtn(ui.btnItems, has)}>انتقال درخواست</button>}
        </div>
      )}
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && reload()}
        style={{ ...ui.searchInput, flex: 1, minWidth: '160px' }} placeholder="جستجو" />
      <CountBadge shown={L.data.length} total={L.pager.total} loading={L.loading} unit="درخواست" />
      <ExcelButton title={TITLE} subtitle={`سال مالی ${toFa(year)}`} columns={shown} rows={L.data} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title={TITLE} subtitle="تدارکات — سامانه ارجان" toolbar={toolbar}
        error={L.error} pager={L.pager} onMore={L.more} loading={L.loading} unit="درخواست">
        {fb.open && (
          <div style={ui.splitWrap}>
            <div style={ui.topPane}>
              <Grid columns={shown} rows={L.data} selectedId={selected && selected.id} onSelect={setSelected}
                onDouble={r => { setSelected(r); if (fbDetail.open) setSub('detail'); }} loading={L.loading}
                empty="درخواستی ثبت نشده است" />
            </div>
            {details.length > 0 && (
              <div style={ui.bottomPane}>
                <Grid columns={dshown} rows={details} zebra="#f5f5dc" />
              </div>
            )}
          </div>
        )}
      </ListPage>

      {modal && (
        <Modal title={modal === 'new' ? 'درخواست جدید' : 'ویرایش درخواست'} onClose={() => setModal(null)}
          footer={<>
            {fb.can('BTNCANCEL') && <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>}
            {fb.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳' : 'ثبت'}</button>}
          </>}>
          {formErr && <div style={{ ...ui.errBox, whiteSpace: 'pre-line' }}>⚠️ {formErr}</div>}
          <div style={ui.row}>
            <Box title="درخواست">
              <Field label="شماره" value={form.request_no} onChange={v => F('request_no', v)} ltr />
              <DateField label="تاریخ" value={form.request_date} onChange={v => F('request_date', v)} />
            </Box>
            <Box title="تاییدیه">
              <Field label="شماره" value={form.confirm_no} onChange={v => F('confirm_no', v)} ltr
                onFocus={() => { if (modal === 'new' && String(form.request_no).trim()) F('confirm_no', form.request_no); }} />
              <DateField label="تاریخ" value={form.confirm_date} onChange={v => F('confirm_date', v)} />
            </Box>
          </div>
          <div style={ui.row}>
            <SelectField label="هزینه ای/تملک" value={Number(form.accounting_kind) || 0} options={ACCOUNTING_KINDS}
              onChange={v => F('accounting_kind', v)} />
            <SelectField label="نوع درخواست" value={Number(form.request_kind) || 0} options={REQUEST_KINDS}
              onChange={v => F('request_kind', v)} disabled={kindLocked} flex={2} />
          </div>
          <div style={ui.row}>
            <Lookup label="درخواست کننده" value={form.asker} endpoint="/goods/lookup/people/"
              onPick={r => setForm(f => ({ ...f, asker_id: r.id, asker: r.title }))} />
            <Lookup label="واحد اجرایی" value={form.department} endpoint="/goods/lookup/departments/"
              onPick={r => setForm(f => ({ ...f, department_id: r.id, department: r.title }))} />
          </div>
          <div style={ui.row}>
            <Lookup label="خودرو" value={form.car} endpoint="/goods/lookup/cars/"
              onPick={r => setForm(f => ({ ...f, car_id: r.id, car: r.title }))} />
            <Lookup label="ساختمان" value={form.building} endpoint="/goods/lookup/buildings/"
              onPick={r => setForm(f => ({ ...f, building_id: r.id, building: r.title }))} />
          </div>
          <div style={{ ...ui.row, alignItems: 'flex-end' }}>
            <Field label="شرح" value={form.remark} onChange={v => F('remark', v)} flex={4} />
            {fb.can(remarkPinned ? 'BTNREMARKPIN' : 'BTNREMARKUNPIN') && (
              <button type="button" onClick={() => setRemarkPinned(p => !p)} title={remarkPinned ? 'شرح برای درخواست بعدی حفظ می‌شود' : 'حفظ شرح برای درخواست بعدی'}
                style={{ ...ui.btnCancel, padding: '8px 10px', background: remarkPinned ? '#fff3cd' : undefined }}>📌</button>
            )}
          </div>
        </Modal>
      )}

      {move && (
        <Modal title="انتقال درخواست" width="380px" onClose={() => setMove(null)}
          footer={<>
            <button onClick={() => setMove(null)} style={ui.btnCancel}>بستن</button>
            <button onClick={movePreview} style={ui.btnSave}>انتقال</button>
          </>}>
          <div style={ui.row}>
            <Field label="به سال مالی" value={move.to_year} onChange={v => setMove({ to_year: v })} ltr />
          </div>
        </Modal>
      )}

      {sub === 'detail' && selected && (
        <SubFormShell>
          <GoodsRequestDetail user={user} onBack={closeDetail} request={selected} />
        </SubFormShell>
      )}
      {sub === 'all' && (
        <SubFormShell>
          <GoodsRequestAllReports user={user} onBack={() => setSub(null)} year={year} />
        </SubFormShell>
      )}
      {ask && <AskBox title={ask.title} lines={ask.lines} danger={ask.danger} onYes={ask.onYes} onNo={ask.onNo || (() => setAsk(null))} />}
      {msg && <MsgBox lines={msg} onClose={() => setMsg(null)} />}
    </>
  );
}
